import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { prepareAuthPopup } from '../config/firebase';

const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { signInWithGoogle, sendEmailLink } = useAuth();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load the Google popup helper as soon as the modal opens, before the user clicks
  useEffect(() => { if (isOpen) prepareAuthPopup(); }, [isOpen]);

  if (!isOpen) return null;

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await signInWithGoogle();
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Google sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      await sendEmailLink(email);
      setEmailSent(true);
    } catch (err) {
      setError('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
      background: 'rgba(10,15,30,0.55)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      animation: 'fadeIn 0.2s ease-out',
    }}>
      {/* Backdrop click */}
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} />

      {/* Modal card */}
      <div style={{
        position: 'relative',
        width: '100%', maxWidth: 420,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255,255,255,0.7)',
        borderRadius: 24,
        boxShadow: '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.9)',
        overflow: 'hidden',
        animation: 'slideUp 0.3s cubic-bezier(0.16,1,0.3,1)',
      }}>

        <div style={{ padding: '32px 32px 28px' }}>
          {/* Close button */}
          <button onClick={onClose} style={{
            position: 'absolute', top: 20, right: 20,
            width: 32, height: 32, borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--ink-400)',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-dark)'; e.currentTarget.style.color = 'var(--ink-700)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--ink-400)'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>

          {!emailSent ? (
            <>
              {/* Header */}
              <div style={{ marginBottom: 28 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: 'var(--brand-blue-tint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16,
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--brand-blue)" strokeWidth="1.75">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/>
                  </svg>
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-900)', marginBottom: 6, letterSpacing: '-0.02em' }}>
                  Welcome
                </h2>
                <p style={{ fontSize: 14, color: 'var(--ink-400)', lineHeight: 1.5 }}>
                  Sign in to continue
                </p>
              </div>

              {/* Google Sign In */}
              <button onClick={handleGoogleSignIn} disabled={loading}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '11px 16px',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  background: 'white',
                  fontSize: 14, fontWeight: 600,
                  color: 'var(--ink-700)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  marginBottom: 16,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-dark)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)'; }}>
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.6 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16.1 19 13 24 13c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.5-11.1-8.2L6.3 33c3.3 6.5 10.1 11 17.7 11z"/>
                  <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.2 5.2C40.7 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-3.9z"/>
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border-light)' }} />
                <span style={{ fontSize: 12, color: 'var(--ink-400)', fontWeight: 500 }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border-light)' }} />
              </div>

              {/* Email sign in */}
              <form onSubmit={handleEmailSubmit}>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: 'var(--ink-600)', marginBottom: 6,
                }}>
                  Email address
                </label>
                <input type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  style={{
                    width: '100%', padding: '10px 14px',
                    borderRadius: 10, border: '1px solid var(--border)',
                    fontSize: 14, color: 'var(--ink-900)',
                    background: 'white', outline: 'none',
                    marginBottom: 12, transition: 'all 0.15s',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onFocus={e => { e.target.style.borderColor = 'var(--brand-blue)'; e.target.style.boxShadow = '0 0 0 3px rgba(27,58,140,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                />
                <button type="submit" disabled={loading || !email}
                  style={{
                    width: '100%', padding: '11px 16px',
                    borderRadius: 10,
                    background: loading || !email ? 'var(--ink-200)' : 'var(--brand-blue)',
                    color: 'white', border: 'none',
                    fontSize: 14, fontWeight: 600,
                    cursor: loading || !email ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onMouseEnter={e => { if (!loading && email) { e.currentTarget.style.background = 'var(--brand-blue-dark)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(27,58,140,0.3)'; }}}
                  onMouseLeave={e => { e.currentTarget.style.background = loading || !email ? 'var(--ink-200)' : 'var(--brand-blue)'; e.currentTarget.style.boxShadow = 'none'; }}>
                  {loading ? 'Sending...' : 'Send magic link'}
                </button>
              </form>

              {error && (
                <div style={{
                  marginTop: 12, padding: '10px 14px',
                  borderRadius: 8, background: 'var(--brand-red-tint)',
                  border: '1px solid rgba(212,31,58,0.15)',
                  fontSize: 13, color: 'var(--brand-red)',
                }}>
                  {error}
                </div>
              )}

              <p style={{ fontSize: 12, color: 'var(--ink-400)', textAlign: 'center', marginTop: 20, lineHeight: 1.5 }}>
                Magic link sent to your email — no password needed
              </p>
            </>
          ) : (
            <div style={{ textAlign: 'center', paddingTop: 8 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: 'var(--brand-blue-tint)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--brand-blue)" strokeWidth="1.75">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <path d="M22 6l-10 7L2 6"/>
                </svg>
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-900)', marginBottom: 8, letterSpacing: '-0.02em' }}>
                Check your inbox
              </h2>
              <p style={{ fontSize: 14, color: 'var(--ink-400)', marginBottom: 6 }}>
                We sent a magic link to
              </p>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-900)', marginBottom: 20 }}>
                {email}
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink-400)', lineHeight: 1.6, marginBottom: 20 }}>
                Click the link in the email to sign in. You can close this window.
              </p>
              <button onClick={() => setEmailSent(false)} style={{
                fontSize: 13, fontWeight: 600,
                color: 'var(--brand-blue)',
                background: 'none', border: 'none',
                cursor: 'pointer', padding: '8px 16px',
                borderRadius: 8,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-blue-tint)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                Use a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
